import type { TFunction } from 'i18next';

import { getImageUrlWithBucketPrefix } from '../../utils/images';
import { formatAmountByCoinType } from '../../utils/prize';
import type { CoinType } from '../cointype/CoinTypeStore';
import { isFreeSpinsBundleItem } from '../rewards/helpers';
import type { BundleItem } from '../rewards/types';

import { selectClosingReward } from './closingReward';
import { clampPct } from './leaderboardRows';
import { resolvePlayableGame } from './playableGame';
import type { BattleClosing, BattleContender, BattlePrizePool, SlotBattle } from './types';

/**
 * The battle's Rive file (`optInImageUrl`) draws every screen from one contract (Rive dev handoff,
 * "Battle of the Slots: wiring guide"): the opening, the win popup, and NEXT TIME for a player the
 * battle paid nothing.
 *
 * - `MainVM` on `Artboard 1` / `State Machine 1`; `user_click` fires on the CTA of every screen.
 * - The screen is the `screen` enum (`THUMBNAILS` / `REWARDS` / `NEXT_TIME`), and nothing draws until
 *   the `activate` trigger fires, after play. An older file has the `endScreen` boolean instead and no
 *   NEXT TIME screen. Counts, enums and the screen are read once at start, so all is set before play.
 * - Thumbnails are not data: the file's image assets `placeholder_0..4` (cards) and `placeholder_w`
 *   (winner) are swapped by name at load. `placeholder_0` is the centre card. Each is fetched first
 *   (`preloadBattleThumbnails`): one that does not load is left out and keeps the file's own art.
 * - The prize area always uses the `PRIZE_POOL` layout (the file defaults to `GRAND_PRIZE`), which
 *   draws both coins; a coin with nothing to show gets an empty string.
 * - Free spins in the prize go to `prizePool/prizePoolFRB` (empty for none), and their game's art to
 *   the image asset `placeholder_frb`. A file without them still draws the opening, but not a win popup
 *   whose reward has free spins: that falls back to the text pane, which lists them.
 */
export type BattleRiveScreen = 'opening' | 'win' | 'next_time';

export const BATTLE_RIVE_PATHS = {
  screen: 'screen',
  /** The older files' screen switch, with no NEXT TIME screen. */
  endScreen: 'endScreen',
  activate: 'activate',
  thumbnailsNum: 'thumbnailsNum',
  rewardGroup: 'prizePool/rewardGroup',
  prizePoolGC: 'prizePool/prizePoolGC',
  prizePoolSC: 'prizePool/prizePoolSC',
  prizePoolFRB: 'prizePool/prizePoolFRB',
  winnerPercentage: 'winnerPercentage',
  cta: 'user_click',
} as const;

/** The `SCREEN` enum's value for each screen. */
export const BATTLE_RIVE_SCREEN_VALUES: Record<BattleRiveScreen, string> = {
  opening: 'THUMBNAILS',
  win: 'REWARDS',
  next_time: 'NEXT_TIME',
};

export const BATTLE_RIVE_PRIZE_POOL = 'PRIZE_POOL';

export const BATTLE_RIVE_WINNER_ASSET = 'placeholder_w';

export const BATTLE_RIVE_FREE_SPINS_ASSET = 'placeholder_frb';

const MIN_THUMBNAILS = 2;
const MAX_THUMBNAILS = 5;

export interface BattleRiveData {
  screen: BattleRiveScreen;
  thumbnailsNum: number;
  /** Image asset name → URL. Assets left out keep the art embedded in the file. */
  images: Record<string, string>;
  prizeGC: string;
  prizeSC: string;
  /** The prize's free spins, e.g. "50 FREE SPINS"; empty for none. */
  prizeFRB: string;
  /** Empty on the opening; set anyway, as the file's own default is placeholder text. */
  winnerPercentage: string;
}

const byDisplayOrder = (contenders: BattleContender[]) =>
  [...contenders].sort((a, b) => a.displayOrder - b.displayOrder);

/** The card art for a slot: what the podium shows, i.e. its stand-in game while its own is down. */
const thumbnailOf = (contender: BattleContender): string | null => {
  const { thumbnailUrl } = resolvePlayableGame(contender);

  return thumbnailUrl ? getImageUrlWithBucketPrefix(thumbnailUrl) : null;
};

const findWinner = (battle: SlotBattle): BattleContender | null => {
  const winner = battle.closing?.winner;

  return winner ? (battle.contenders.find((contender) => contender.gameId === winner.gameId) ?? null) : null;
};

export interface PrizeFreeSpins {
  /** All the prize's free spins, across its bundles. */
  count: number;
  /** The first bundle's game art, for the prize area. */
  imageUrl: string | null;
}

/** The free spins a prize carries (mini-game rounds among them, as the claim animation counts them), or null. */
export function selectPrizeFreeSpins(bundleItems: BundleItem[] | undefined): PrizeFreeSpins | null {
  const payloads = (bundleItems ?? []).filter(isFreeSpinsBundleItem).map((item) => item.bundlePayload);
  const count = payloads.reduce((total, payload) => total + (payload.displayCount ?? payload.rounds ?? 0), 0);

  if (count <= 0) {
    return null;
  }

  const image = payloads.find((payload) => payload.gameImageUrl || payload.imageUrl);

  return { count, imageUrl: image ? image.gameImageUrl || image.imageUrl : null };
}

/** The closing's screen: NEXT TIME when the battle paid this player nothing, the win popup otherwise. */
export const closingRiveScreen = (closing: BattleClosing): Exclude<BattleRiveScreen, 'opening'> =>
  closing.outcome === 'lost_no_reward' ? 'next_time' : 'win';

/**
 * The opening advertises the segment's grand prize. The win popup shows what CLAIM credits — the
 * player's own reward, not the pool, which would read as all of it being theirs. A pool share whose
 * package view could not be read falls back to `rewardAmount` in the run's coin.
 */
export const selectBattleRivePrize = (battle: SlotBattle, screen: BattleRiveScreen): BattlePrizePool => {
  if (screen === 'opening') {
    return {
      gc: battle.prizePool?.gc ?? null,
      sc: battle.prizePool?.sc ?? null,
      bundleItems: battle.prizePool?.bundleItems ?? [],
    };
  }

  if (screen === 'next_time') {
    return { gc: null, sc: null, bundleItems: [] };
  }

  const closing = battle.closing;
  const reward = closing ? selectClosingReward(closing) : null;

  if (reward) {
    return { gc: reward.gcCoin, sc: reward.scCoin, bundleItems: reward.bundleItems };
  }

  const share = closing?.rewardType === 'pool' ? closing.rewardAmount : null;

  return battle.coinType === 'GC' ? { gc: share, sc: null, bundleItems: [] } : { gc: null, sc: share, bundleItems: [] };
};

/** Only a `.riv` is a battle Rive: a run still holding the opening image it had before has none. */
export function hasBattleRive(battle: SlotBattle): boolean {
  return Boolean(battle.optInImageUrl && /\.riv(?:[?#].*)?$/i.test(battle.optInImageUrl));
}

/**
 * Whether the battle can be drawn on this screen at all: a Rive file, and for a closing a winner that is
 * one of the battle's contenders (the win popup also needs a coin prize). False sends the opening to be skipped and the win popup to
 * its fallback pane.
 */
export function canDrawBattleRive(battle: SlotBattle, screen: BattleRiveScreen): boolean {
  if (!hasBattleRive(battle)) {
    return false;
  }

  if (screen === 'opening') {
    return true;
  }

  if (screen === 'next_time') {
    return battle.closing?.outcome === 'lost_no_reward' && findWinner(battle) !== null;
  }

  // The prize area draws coins and free spins only: a reward with neither (say, a metagame-only package)
  // would show an empty prize over CLAIM, so it goes to the fallback, which lists what the claim credits.
  const prize = selectBattleRivePrize(battle, 'win');
  const hasPrize = Boolean(prize.gc || prize.sc) || selectPrizeFreeSpins(prize.bundleItems) !== null;

  return Boolean(battle.closing) && findWinner(battle) !== null && hasPrize;
}

const formatCoin = (amount: number | null | undefined, coinType: CoinType, t: TFunction): string =>
  amount ? formatAmountByCoinType({ amount, coinType, t }) : '';

export function buildBattleRiveData(battle: SlotBattle, screen: BattleRiveScreen, t: TFunction): BattleRiveData {
  const contenders = byDisplayOrder(battle.contenders).slice(0, MAX_THUMBNAILS);
  const images: Record<string, string> = {};

  contenders.forEach((contender, index) => {
    const url = thumbnailOf(contender);

    if (url) {
      images[`placeholder_${index}`] = url;
    }
  });

  const winner = screen === 'opening' ? null : findWinner(battle);
  const winnerUrl = winner ? thumbnailOf(winner) : null;

  if (winnerUrl) {
    images[BATTLE_RIVE_WINNER_ASSET] = winnerUrl;
  }

  const prize = selectBattleRivePrize(battle, screen);
  const freeSpins = selectPrizeFreeSpins(prize.bundleItems);

  if (freeSpins?.imageUrl) {
    images[BATTLE_RIVE_FREE_SPINS_ASSET] = getImageUrlWithBucketPrefix(freeSpins.imageUrl);
  }

  const sharePct = screen === 'opening' ? undefined : battle.closing?.winner?.sharePct;

  return {
    screen,
    thumbnailsNum: Math.min(Math.max(contenders.length, MIN_THUMBNAILS), MAX_THUMBNAILS),
    images,
    prizeGC: formatCoin(prize.gc, 'GC', t),
    prizeSC: formatCoin(prize.sc, 'SC', t),
    prizeFRB: freeSpins ? t('slotBattle.prizeFreeSpins', { count: freeSpins.count }) : '',
    // Same label as the podium's share, so the winner reads as it did there.
    winnerPercentage: sharePct === undefined ? '' : t('slotBattle.sharePct', { pct: clampPct(sharePct).toFixed(1) }),
  };
}

/** How long the thumbnails get, all together, before the file loads without the ones still out. */
export const BATTLE_THUMBNAIL_PRELOAD_TIMEOUT_MS = 3000;

export interface SkippedBattleThumbnail {
  asset: string;
  url: string;
  reason: string;
}

export interface PreloadedBattleThumbnails {
  /** Image asset name → URL, for the thumbnails that came back: the only ones worth swapping in. */
  loaded: Record<string, string>;
  skipped: SkippedBattleThumbnail[];
}

/**
 * Fetches each thumbnail before the file loads, as the swap itself does (same request, so the swap
 * then reads it from the HTTP cache). The file's asset loader has to claim or leave an asset on the
 * spot, and a claimed asset whose swap fails is a blank card — so only thumbnails known to load are
 * claimed. A missing one (a 404 in the bucket), a network error or one still out at the deadline is
 * skipped, never thrown: its card keeps the art embedded in the file. Callers pass allowed URLs only.
 */
export async function preloadBattleThumbnails(
  images: Record<string, string>,
  timeoutMs: number = BATTLE_THUMBNAIL_PRELOAD_TIMEOUT_MS,
): Promise<PreloadedBattleThumbnails> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  // The winner is also one of the cards: fetch each URL once.
  const requests = new Map<string, Promise<string | null>>();

  const load = async (url: string): Promise<string | null> => {
    try {
      const response = await fetch(url, { signal: controller.signal });

      if (!response.ok) {
        return `HTTP ${response.status}`;
      }

      await response.blob();
      return null;
    } catch (error) {
      if (controller.signal.aborted) {
        return `timed out after ${timeoutMs} ms`;
      }

      return error instanceof Error ? error.message : String(error);
    }
  };

  try {
    const outcomes = await Promise.all(
      Object.entries(images).map(async ([asset, url]) => {
        let request = requests.get(url);

        if (!request) {
          request = load(url);
          requests.set(url, request);
        }

        return { asset, url, reason: await request };
      }),
    );

    const loaded: Record<string, string> = {};
    const skipped: SkippedBattleThumbnail[] = [];

    outcomes.forEach(({ asset, url, reason }) => {
      if (reason === null) {
        loaded[asset] = url;
      } else {
        skipped.push({ asset, url, reason });
      }
    });

    return { loaded, skipped };
  } finally {
    clearTimeout(timer);
  }
}
